/**
 * API utility functions for RCC Catalog
 */
import { getOrgId, getStoreId, getCatalogId as getTenantCatalogId, getScreenId } from '../utils/tenantBoot';

const getBaseUrl = () => {
  // Priority 1: URL parameter (for network access from other devices)
  const urlParams = new URLSearchParams(window.location.search);
  const urlApiBase = urlParams.get('api_base');
  
  if (urlApiBase) {
    localStorage.setItem('rcc_api_base', urlApiBase);
    return urlApiBase;
  }
  
  // Priority 2: LocalStorage (persisted from previous URL param)
  const storedApiBase = localStorage.getItem('rcc_api_base');
  if (storedApiBase) {
    return storedApiBase;
  }
  
  // Priority 3: Environment variable (empty = relative paths via Vite proxy)
  return import.meta.env.VITE_RCC_BASE || '';
};

/**
 * Resolve catalog_id from slug by calling backend
 */
const resolveCatalogBySlug = async (slug) => {
  try {
    const orgId = getOrgId();
    const url = `${getBaseUrl()}/rcc-api/catalog/resolve?slug=${encodeURIComponent(slug)}&org_id=${orgId}`;
    const response = await fetch(url);
    if (!response.ok) {
      console.error('[api] Failed to resolve catalog by slug:', slug);
      return null;
    }
    const data = await response.json();
    return data.catalog_id;
  } catch (error) {
    console.error('[api] Error resolving catalog by slug:', error);
    return null;
  }
};

/**
 * Resolve catalog_id from domain by calling backend
 * Used for website mode when accessing via domain (e.g., localhost:5400, example.com)
 */
const resolveCatalogByDomain = async (domain) => {
  try {
    const orgId = getOrgId();
    const url = `${getBaseUrl()}/rcc-api/catalog/resolve?domain=${encodeURIComponent(domain)}&org_id=${orgId}`;
    console.log('[api] Resolving catalog by domain:', domain);
    const response = await fetch(url);
    if (!response.ok) {
      console.error('[api] Failed to resolve catalog by domain:', domain);
      return null;
    }
    const data = await response.json();
    console.log('[api] Resolved catalog_id:', data.catalog_id, 'for domain:', domain);
    return data.catalog_id;
  } catch (error) {
    console.error('[api] Error resolving catalog by domain:', error);
    return null;
  }
};

/**
 * Get catalog_id for the current page.
 *
 * Kiosk mode: delegates entirely to tenantBoot's screen_id → resolve-by-screen
 * resolution (single authoritative source - no localStorage fallback, so a
 * stale value from a different screen_id can never leak in).
 *
 * Website mode priority:
 * 1. catalog_id URL parameter (explicit)
 * 2. Slug from URL path
 * 3. Domain resolution (auto-detect catalog from domain)
 * 4. null (show all products - fallback for testing/development)
 */
export const getCatalogId = async () => {
  const params = new URLSearchParams(window.location.search);
  const isKioskMode = params.get('kiosk') === '1';

  if (isKioskMode) {
    // Single authoritative source for kiosk catalogue identity: the current
    // page's screen_id, already resolved once via tenantBoot's boot() →
    // resolve-by-screen. Never fall back to a shared/stale localStorage
    // value here - that was the cross-screen catalogue leak.
    return getTenantCatalogId();
  }

  // Use different localStorage keys for kiosk vs website
  const storageKey = 'rcc_catalog_website_id';
  
  // Check URL parameter first
  const urlCatalogId = params.get('catalog_id');
  if (urlCatalogId) {
    // Store in mode-specific localStorage
    localStorage.setItem(storageKey, urlCatalogId);
    console.log(`[api] catalog_id from URL, stored in ${storageKey}:`, urlCatalogId);
    return urlCatalogId;
  }
  
  // Check for slug in URL path (website mode only)
  if (!isKioskMode) {
    const path = window.location.pathname;
    if (path && path !== '/' && path !== '/index.html') {
      const slug = path.replace(/^\//, '').replace(/\/$/, '');
      if (slug) {
        console.log('[api] Detected slug in URL:', slug);
        const catalogId = await resolveCatalogBySlug(slug);
        if (catalogId) {
          localStorage.setItem(storageKey, catalogId);
          localStorage.setItem('rcc_catalog_slug', slug);
          return catalogId;
        }
      }
    }
  }
  
  // Try domain resolution (website mode only)
  if (!isKioskMode) {
    const domain = window.location.host; // e.g., "localhost:5400" or "example.com"
    console.log('[api] Attempting domain resolution for:', domain);
    const catalogId = await resolveCatalogByDomain(domain);
    if (catalogId) {
      localStorage.setItem(storageKey, catalogId);
      console.log('[api] Resolved catalog_id from domain:', catalogId);
      return catalogId;
    }
    // Clear storage if no catalog found for this domain
    localStorage.removeItem(storageKey);
    console.log('[api] No catalog found for domain - showing all products');
  }
  
  // No catalog context = show all products (fallback for testing/development)
  console.log('[api] No catalog context - showing all products');
  return null;
};

const apiCall = async (endpoint, options = {}) => {
  const url = `${getBaseUrl()}${endpoint}`;
  const response = await fetch(url, {
    headers: {
      'Content-Type': 'application/json',
      ...options.headers,
    },
    ...options,
  });

  if (!response.ok) {
    const errorText = await response.text();
    throw new Error(errorText || `API error: ${response.status}`);
  }

  return response.json();
};

/**
 * Fetch all categories
 * @param {String} mode - "catalog" for kiosk mode, "ecommerce" for web
 */
export const fetchCategories = async (mode = 'ecommerce') => {
  const catalogId = await getCatalogId();
  const orgId = getOrgId();
  const catalogParam = catalogId ? `&catalog_id=${catalogId}` : '';
  return apiCall(`/rcc-api/catalog/categories?mode=${mode}&org_id=${orgId}${catalogParam}`);
};

/**
 * Fetch all products or products by category
 * @param {String} categoryId - Optional category ID filter
 * @param {String} mode - "catalog" for kiosk mode, "ecommerce" for web
 * @param {String} variantDisplay - "grouped" or "expanded" - controls variant product visibility
 */
export const fetchProducts = async (categoryId = null, mode = 'ecommerce', variantDisplay = 'grouped') => {
  const catalogId = await getCatalogId();
  const orgId = getOrgId();
  const catalogParam = catalogId ? `&catalog_id=${catalogId}` : '';
  const endpoint = categoryId
    ? `/rcc-api/catalog/products?category_id=${categoryId}&mode=${mode}&variant_display=${variantDisplay}&org_id=${orgId}${catalogParam}`
    : `/rcc-api/catalog/products?mode=${mode}&variant_display=${variantDisplay}&org_id=${orgId}${catalogParam}`;
  return apiCall(endpoint);
};

/**
 * Fetch single product by ID
 */
export const fetchProductById = async (productId) => {
  return apiCall(`/rcc-api/catalog/products/${productId}`);
};

/**
 * Create a catalog order and get 4-digit code
 * @param {Array} items - Array of {barcode, qty}
 * @param {String} mobileNumber - Optional mobile number
 * @param {String} orgId - Organization ID
 * @param {String} storeId - Store ID
 * @param {String} orderSource - Order source (kiosk, website, ecommerce)
 * @param {String} websiteId - Website identifier for tracking
 */
export const createCatalogOrder = async (items, mobileNumber = null, orgId = null, storeId = null, orderSource = 'website', websiteId = null) => {
  return apiCall('/rcc-api/catalog/orders', {
    method: 'POST',
    body: JSON.stringify({
      items,
      mobile_number: mobileNumber,
      org_id: orgId || getOrgId(),
      store_id: storeId || getStoreId() || null,
      order_source: orderSource,
      website_id: websiteId,
      // Trusted identifiers - backend derives org_id/store_id from these
      // when present and ignores the client-supplied org_id/store_id above.
      screen_id: getScreenId(),
      catalog_id: getTenantCatalogId(),
    }),
  });
};

/**
 * Fetch order by 4-digit code
 */
export const fetchOrderByCode = async (code) => {
  return apiCall(`/rcc-api/catalog/orders/${code}?org_id=${getOrgId()}`);
};

/**
 * Claim an order (mark it as loaded into till)
 */
export const claimOrder = async (code) => {
  return apiCall(`/rcc-api/catalog/orders/${code}/claim`, {
    method: 'POST',
  });
};

/**
 * Get pricing preview for basket items with live promotion calculations
 * Validates current prices and applies promotions server-side
 */
export const getCartPreview = async (items, orgId = null, storeId = null) => {
  try {
    const resolvedStoreId = storeId || getStoreId() || null;
    return apiCall('/rcc-api/catalog/cart-preview', {
      method: 'POST',
      body: JSON.stringify({
        items,
        org_id: orgId || getOrgId(),
        store_id: resolvedStoreId,
        screen_id: getScreenId(),
        catalog_id: getTenantCatalogId(),
      }),
    });
  } catch (err) {
    console.error('Cart preview failed:', err);
    throw err;
  }
};

/**
 * Search products by name or barcode
 * @param {String} query - Search query
 * @param {String} mode - "catalog" for kiosk mode, "ecommerce" for web
 */
export const searchProducts = async (query, mode = 'ecommerce', limit = 50) => {
  if (!query || query.trim().length < 2) return [];
  
  try {
    const catalogId = await getCatalogId();
    const orgId = getOrgId();
    const catalogParam = catalogId ? `&catalog_id=${catalogId}` : '';
    // Fetch all products (backend doesn't have search parameter yet)
    const allProducts = await apiCall(`/rcc-api/catalog/products?mode=${mode}&org_id=${orgId}${catalogParam}`);
    
    // Filter client-side
    const searchTerm = query.trim().toLowerCase();
    const filtered = allProducts.filter(product => {
      const name = (product.product_name || product.name || '').toLowerCase();
      const barcode = (product.barcode || '').toLowerCase();
      const category = (product.category_name || '').toLowerCase();
      const skuCode = (product.sku_code || '').toLowerCase();
      
      return name.includes(searchTerm) || 
             barcode.includes(searchTerm) || 
             category.includes(searchTerm) ||
             skuCode.includes(searchTerm);
    });
    
    // Return limited results
    return filtered.slice(0, limit);
  } catch (err) {
    console.error('Search failed:', err);
    return [];
  }
};

/**
 * Fetch variant groups for products
 * Returns groups with their linked products and attributes
 * PUBLIC endpoint - no auth required
 */
export const fetchVariantGroups = async (orgId = null) => {
  try {
    return apiCall(`/rcc-api/catalog/variant-groups?org_id=${orgId || getOrgId()}`);
  } catch (err) {
    console.error('Failed to fetch variant groups:', err);
    return { groups: [] };
  }
};

/**
 * Fetch detailed variant group with products
 */
export const fetchVariantGroupDetails = async (groupId) => {
  try {
    return apiCall(`/rcc-api/variant-groups/${groupId}`);
  } catch (err) {
    console.error('Failed to fetch variant group details:', err);
    return null;
  }
};

/**
 * Fetch active promotions with groups and items (including barcodes)
 * Returns promotions, groups, and group_items for matching products
 * PUBLIC endpoint - no auth required
 */
export const fetchActivePromotions = async (orgId = null, storeId = null) => {
  try {
    let endpoint = `/rcc-api/promotions/active-bundle?org_id=${orgId || getOrgId()}`;
    if (storeId) {
      endpoint += `&store_id=${storeId}`;
    }
    return apiCall(endpoint);
  } catch (err) {
    console.error('Failed to fetch active promotions:', err);
    return { promotions: [], groups: [], group_items: [] };
  }
};
